import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Loader from '../../components/helpers/Loader';
import './FeedbackPage.css';

const FeedbackPage = () => {
  const navigate = useNavigate();

  // State variables
  const [feedback, setFeedback] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalFeedback, setTotalFeedback] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const fetchFeedbackData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/support/get-all-feedback`, { credentials: 'include', });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      console.log("Fetched feedback data:", data);
      let filteredData = data.map(item => ({
        ...item,
        contactNo: item.phone // Map phone to contactNo for consistency
      }));

      // Apply search filter
      if (searchTerm) {
        filteredData = filteredData.filter(item =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item.phone && item.phone.includes(searchTerm))
        );
      }

      // Apply sorting
      filteredData.sort((a, b) => {
        const aValue = a[sortColumn];
        const bValue = b[sortColumn];
        if (sortOrder === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });

      // Apply pagination
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedData = filteredData.slice(startIndex, endIndex);

      setFeedback(paginatedData);
      setTotalFeedback(filteredData.length);
      setIsLoading(false);
    } catch (err) {
      console.error("Failed to fetch feedback:", err);
      setError(err);
    }
  }, [page, limit, searchTerm, sortColumn, sortOrder]);

  useEffect(() => {
    fetchFeedbackData();
  }, [fetchFeedbackData]);

  // Handlers
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleSort = (column) => {
    const isAsc = sortColumn === column && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortColumn(column);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleRowClick = (feedbackId) => {
    navigate(`/feedback/${feedbackId}`);
  };

  // Helper for rendering sort indicator
  const renderSortIndicator = (column) => {
    if (sortColumn === column) {
      return <span className={`sort-icon ${sortOrder}`}></span>;
    }
    return null;
  };

  // Helper to truncate long text
  const truncateText = (text, maxLength = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className="feedback-page-container">
      <div className="page-header">
        <h2>Feedback Management</h2>
        <p className="page-subtitle">Click on any row to view detailed feedback information</p>
      </div>

      <div className="table-controls">
        <input
          type="text"
          placeholder="Search feedback..."
          className="search-input"
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </div>

      {isLoading ? (
        <div className="loading-container">
          <div className="loading-content">
            <Loader size={50} color="#007bff" secondaryColor="#f3f3f3" />
            <p>Loading feedback...</p>
          </div>
        </div>
      ) : error ? (
        <div className="error-message">
          <p>Failed to load feedback data.</p>
          <p>Error: {error.message || 'Unknown error'}</p>
        </div>
      ) : (
        <>
          <div className="table-wrapper">
            <table className="feedback-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')}>
                    Name {renderSortIndicator('name')}
                  </th>
                  <th onClick={() => handleSort('email')}>
                    Email {renderSortIndicator('email')}
                  </th>
                  <th onClick={() => handleSort('contactNo')}>
                    Contact No {renderSortIndicator('contactNo')}
                  </th>
                  <th onClick={() => handleSort('description')}>
                    Feedback {renderSortIndicator('description')}
                  </th>
                  <th onClick={() => handleSort('createdAt')}>
                    Date {renderSortIndicator('createdAt')}
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {feedback.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                      No feedback found.
                    </td>
                  </tr>
                ) : (
                  feedback.map((item) => (
                    <tr
                      key={item.id}
                      className="feedback-row"
                      onClick={() => handleRowClick(item.id)}
                    >
                      <td>
                        <div className="name-cell">
                          {/* <div className="user-avatar-small">
                            {item.name.charAt(0).toUpperCase()}
                          </div> */}
                          {item.name}
                        </div>
                      </td>
                      <td>{item.email}</td>
                      <td>{item.contactNo || 'N/A'}</td>
                      <td>
                        <div className="feedback-preview">
                          {truncateText(item.description)}
                        </div>
                      </td>
                      <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                      <td>
                        <button
                          className="view-details-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(item.id);
                          }}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination-controls">
            <span className="pagination-info">
              Rows per page:
              <select
                className="rows-per-page-select"
                value={limit}
                onChange={handleLimitChange}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
              <span>{(page - 1) * limit + 1}-{Math.min(page * limit, totalFeedback)} of {totalFeedback}</span>
            </span>
            <div className="pagination-buttons">
              <button
                className="pagination-button"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                Previous
              </button>
              <button
                className="pagination-button"
                onClick={() => handlePageChange(page + 1)}
                disabled={page * limit >= totalFeedback}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FeedbackPage;